/**
 * Query-bar VALUE autocomplete — the real (browser-only) value suggester.
 *
 * `suggestValues(fieldKey, term, { scopePath, range, limit })` returns up to
 * `limit` `{ value, label, detail }` rows for the value being typed in a
 * `field:`/`field!=` clause. Injected into QueryBar (which stays free of these
 * deps + node-test-safe); the value-context parsing lives in queryLang.js
 * (`parseValueContext`, pure).
 *
 * Sources, by field:
 *   - cluster → the in-memory cluster tree (loadClusterTree, promise-cached):
 *     substring match on names. This is exactly the set `resolveAndCompile`'s
 *     clusterResolver will match, and the picked NAME is what gets inserted
 *     (quoted upstream), resolved to id(s) at query time. No server round-trip.
 *   - every other dim-backed field (agent, endpoint, model, session, identity,
 *     tool, flag, skill, mcp_server, memory) → executeDimensionEntries via the
 *     cached `query("getDimensionEntries", ...)`, the same analytics-only path
 *     the sidebar uses.
 *   - fields NOT in `isValueSuggestField` (queryLang.js) → no suggestions:
 *     `file` (dim "files" isn't analytics-supported → would short-circuit to
 *     empty), message_type, tool_result, and the numeric / full-text fields.
 *
 * Scope-aware: suggestions are drawn from the caller's current scope + range, so
 * you see the values that actually occur in the view you're refining.
 */

import { QUERY_FIELDS, isValueSuggestField, resolveField } from "./queryLang.js";
import { PROVIDER_TAG_TO_IDENTITY } from "./agentIdentity.js";
import { query } from "./dataClient.js";
import { loadClusterTree } from "./clusterTree.js";

// Compact count for the secondary "detail" hint (1234 → "1.2k").
function compact(n) {
  const x = Number(n) || 0;
  if (x >= 1e6) return `${(x / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (x >= 1e3) return `${(x / 1e3).toFixed(1).replace(/\.0$/, "")}k`;
  return String(x);
}

// Dims whose ON-THE-WIRE filter value is an opaque id while the human-readable
// thing is a separate label: insert the id, show the label. Every other dim is
// value-is-label (or, for endpoint, matched by its hostname label), so insert
// the label. `identity` is NOT here: its clause searches the person's
// display_name / email / upn (see queryLangCompile.js), so inserting the display
// name matches — inserting the raw source_id would render a cryptic id in the
// bar. (The compiler still matches a pasted or saved-link source_id exactly on
// any provider, so a hand-typed id resolves; autocomplete just prefers the
// readable form.)
const ID_VALUED = new Set(["session"]);

// QUERY_FIELDS carry the SINGULAR dim (`endpoint`), but the getDimensionEntries
// query is keyed by the PLURAL sidebar dim — its `shortCircuit` returns an empty
// result for any dimKey not in SUPPORTED_DIMS (which holds plurals), BEFORE
// reaching executeDimensionEntries. Passing the singular key silently yields
// zero suggestions. This is the inverse of apiClient's DIM_SINGULAR for the
// value-suggestable dims.
const DIM_PLURAL = {
  agent: "agents", provider: "providers", endpoint: "endpoints", model: "models", session: "sessions",
  identity: "identities", department: "groups", tool_call: "tools",
  skill: "skills", mcp_server: "mcp_servers", memory: "memories",
};

async function suggestClusters(term, limit) {
  const tree = await loadClusterTree();
  const needle = term.trim().toLowerCase();
  const hits = [];
  for (const node of tree.byId.values()) {
    const name = String(node.name ?? "");
    if (!name) continue;
    const lower = name.toLowerCase();
    if (needle && !lower.includes(needle)) continue;
    hits.push({ id: String(node.id), name, prefix: needle ? lower.startsWith(needle) : false });
  }
  // Prefix matches first, then alphabetical — stable, useful ordering.
  hits.sort((a, b) => (b.prefix - a.prefix) || a.name.localeCompare(b.name));
  return hits.slice(0, limit).map((h) => ({ value: h.name, label: h.name, detail: `#${h.id}` }));
}

async function suggestDimEntries(def, term, { scopePath = [], range, limit }) {
  const { entries } = await query(
    "getDimensionEntries",
    {
      dimKey: DIM_PLURAL[def.dim] ?? def.dim,
      parentScopePath: scopePath,
      search: term,
      range,
      limit,
      sort: "activity",
      // Labels only: skip the total-count + sparkline queries (a dropdown
      // doesn't need them) — one analytics query instead of three. (session /
      // identity still overlay their human labels, so those cost rank + labels.)
      minimal: true,
    },
    { cache: true, staleMs: 30_000 },
  );
  const insertId = ID_VALUED.has(def.dim);
  return (entries || []).flatMap((e) => {
    const label = String(e.label ?? "");
    const id = String(e.id ?? "");
    // Strict: insert exactly the value the compiler matches on. For label-valued
    // dims (incl. endpoint, matched by hostname) that's the label; an entry with
    // no label is dropped rather than inserting an id the filter wouldn't match.
    let value = insertId ? id : label;
    if (!value) return [];
    let display = label || id;
    // Agent values are raw provider tags (microsoft, anthropic, …). Show — and
    // insert — the same friendly name the breakdown shows (Copilot, Claude); it
    // normalizes back to the tag in queryLang, so the steer stays valid AND its
    // labels match the dim. Unknown tags (no identity) stay raw.
    if (def.dim === "agent") {
      const friendly = PROVIDER_TAG_TO_IDENTITY[value.toLowerCase()]?.name;
      if (friendly) { display = friendly; value = friendly; }
    }
    return [{ value, label: display, detail: e.activity != null ? compact(e.activity) : "" }];
  });
}

export async function suggestValues(fieldKey, term, opts = {}) {
  const key = resolveField(fieldKey)?.key || fieldKey;
  const def = QUERY_FIELDS[key];
  // Only fields with an enumerable, supported source (isValueSuggestField —
  // excludes `file`, message_type, numeric/text) — keeps suggester behavior in
  // lockstep with the QueryBar's value-mode gate and the advertised set.
  if (!def || !isValueSuggestField(key)) return [];
  const limit = opts.limit ?? 8;
  try {
    if (def.kind === "cluster") return await suggestClusters(String(term ?? ""), limit);
    return await suggestDimEntries(def, String(term ?? ""), { ...opts, limit });
  } catch (err) {
    // Autocomplete is best-effort: a failed lookup yields no suggestions, never
    // an error in the bar. Surfaced as a warning so a silently-empty dropdown is
    // debuggable (e.g. a planner rejection on a particular dim/scope).
    if (typeof console !== "undefined") console.warn(`[queryLangSuggest] ${key} value lookup failed:`, err);
    return [];
  }
}
